import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Phone, Calendar, Tag } from "lucide-react";
import { Header, Footer, StickyCTA } from "../components/Layout";
import articles from "../data/blogArticles";

function BlogArticle() {
  const { articleId } = useParams();
  const staticArticle = articles[articleId];
  const [aiArticle, setAiArticle] = useState(null);
  const [aiLoading, setAiLoading] = useState(!staticArticle);

  useEffect(() => {
    if (staticArticle) return;
    setAiLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/articles/slug/${articleId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setAiArticle(data))
      .catch(() => setAiArticle(null))
      .finally(() => setAiLoading(false));
  }, [articleId, staticArticle]);

  const article = staticArticle || aiArticle;

  if (!article && aiLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Chargement de l'article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
          <p className="text-gray-600 mb-6">L'article que vous cherchez n'existe pas ou a été déplacé.</p>
          <Link to="/blog" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-semibold">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  // Convert markdown-style content to HTML
  const formatContent = (content) => {
    return content
      .split('\n')
      .map((line, index) => {
        // Skip first H1 (already in header)
        if (line.startsWith('# ') && index < 5) {
          return null;
        }
        // H2
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-4 mt-10">{line.substring(3)}</h2>;
        }
        // H3
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-xl font-bold text-emerald-600 mb-3 mt-6">{line.substring(4)}</h3>;
        }
        // Bold text with **
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={index} className="font-bold text-lg text-[#0f172a] mb-2">{line.slice(2, -2)}</p>;
        }
        // List items
        if (line.startsWith('- ')) {
          return (
            <li key={index} className="ml-6 mb-2 text-gray-700 flex items-start">
              <span className="text-emerald-500 mr-2 mt-1">•</span>
              <span>{line.substring(2)}</span>
            </li>
          );
        }
        // Empty line
        if (line.trim() === '') {
          return <div key={index} className="h-3"></div>;
        }
        // Regular paragraph
        return <p key={index} className="text-gray-700 leading-relaxed mb-4">{line}</p>;
      })
      .filter(Boolean);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <Helmet>
        <title>{`${article.title} | Azur Tech`}</title>
        <meta name="description" content={article.metaDescription || article.excerpt} />
        <link rel="canonical" href={`https://azurtech06.fr/blog/${articleId}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={`https://azurtech06.fr/blog/${articleId}`} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            author: { "@type": "Organization", name: "Azur Tech", url: "https://azurtech06.fr" },
            publisher: { "@type": "Organization", "@id": "https://azurtech06.fr/#organization", name: "Azur Tech" },
            mainEntityOfPage: `https://azurtech06.fr/blog/${articleId}`,
            articleSection: article.category,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://azurtech06.fr/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://azurtech06.fr/blog" },
              { "@type": "ListItem", position: 3, name: article.title, item: `https://azurtech06.fr/blog/${articleId}` },
            ],
          },
        ])}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 bg-gradient-to-br from-[#0f172a] to-[#1e3a5f]">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <div className="mb-6 text-sm">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Accueil</Link>
            <span className="mx-2 text-gray-500">/</span>
            <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link>
            <span className="mx-2 text-gray-500">/</span>
            <span className="text-emerald-400">{article.title}</span>
          </div>

          <div className="max-w-4xl">
            {article.category && (
              <span className="inline-flex items-center bg-emerald-500 text-white text-sm font-medium px-3 py-1 rounded-full mb-4">
                <Tag className="w-3 h-3 mr-1" />
                {article.category}
              </span>
            )}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {article.title}
            </h1>
            <div className="flex items-center text-gray-400">
              <Calendar className="w-4 h-4 mr-2" />
              <span>{article.date}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main content */}
            <article className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6 md:p-10">
              <div className="prose prose-lg max-w-none">
                {formatContent(article.content)}
              </div>

              {/* CTA Box */}
              <div className="mt-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-8 text-center text-white">
                <h3 className="text-2xl font-bold mb-4">
                  Besoin d'une intervention ?
                </h3>
                <p className="text-lg mb-6 text-emerald-100">
                  Nos experts sont disponibles 24h/24 - 7j/7
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="tel:0764079535"
                    className="bg-white text-emerald-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-all inline-flex items-center justify-center"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    07 64 07 95 35
                  </a>
                  <Link
                    to="/#devis"
                    className="bg-emerald-700 text-white px-8 py-4 rounded-xl font-bold hover:bg-emerald-800 transition-all"
                  >
                    Demander un devis
                  </Link>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                {/* Contact card */}
                <div className="bg-[#0f172a] rounded-2xl p-6 text-white text-center">
                  <h3 className="font-bold text-lg mb-2">Un problème ?</h3>
                  <p className="text-gray-400 text-sm mb-4">Appelez-nous maintenant</p>
                  <a 
                    href="tel:0764079535" 
                    className="text-2xl font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    07 64 07 95 35
                  </a>
                  <p className="text-emerald-400 text-sm mt-2">Disponible 24h/24</p>
                </div>

                {/* Related articles */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="font-bold text-lg text-[#0f172a] mb-4">Articles populaires</h3>
                  <ul className="space-y-3">
                    {Object.entries(articles)
                      .filter(([id]) => id !== articleId)
                      .slice(0, 4)
                      .map(([id, art]) => (
                        <li key={id}>
                          <Link 
                            to={`/blog/${id}`}
                            className="text-gray-600 hover:text-emerald-600 transition-colors text-sm block"
                          >
                            → {art.title}
                          </Link>
                        </li>
                      ))
                    }
                  </ul>
                  <Link 
                    to="/blog" 
                    className="mt-4 block text-center text-emerald-600 font-semibold text-sm hover:text-emerald-700"
                  >
                    Voir tous les articles
                  </Link>
                </div>

                {/* Services */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="font-bold text-lg text-[#0f172a] mb-4">Nos services</h3>
                  <ul className="space-y-2">
                    <li><Link to="/desinsectisation" className="text-gray-600 hover:text-emerald-600">→ Désinsectisation</Link></li>
                    <li><Link to="/deratisation" className="text-gray-600 hover:text-emerald-600">→ Dératisation</Link></li>
                    <li><Link to="/punaises-de-lit" className="text-gray-600 hover:text-emerald-600">→ Punaises de lit</Link></li>
                    <li><Link to="/desinfection" className="text-gray-600 hover:text-emerald-600">→ Désinfection</Link></li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>

          {/* Back to blog */}
          <div className="mt-8">
            <Link to="/blog" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-semibold">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux articles
            </Link>
          </div>
        </div>
      </div>

      <Footer />
      <StickyCTA />
    </div>
  );
}

export default BlogArticle;
